import { Input } from "@storybook"; // Importing Input component from Storybook
import { FC, Fragment, useCallback, useMemo } from "react"; // Importing necessary React hooks and types

interface IFormContainer {
  form: any; // Array of form fields to be rendered
  handleChange: (e: any) => void; // Callback function to handle changes in form fields
  handleChangeCountry?: any; // Optional callback function for handling country code changes
}

/**
 * FormContainer component renders a list of form fields based on the provided form configuration.
 *
 * @param {IFormContainer} props - The properties object for the FormContainer component.
 * @param {any} props.form - The array of form field configurations.
 * @param {(e: any) => void} props.handleChange - The callback function to handle form field changes.
 * @param {any} [props.handleChangeCountry] - Optional callback function to handle country code changes.
 *
 * @returns {JSX.Element} The rendered FormContainer component with form fields.
 */
export const FormContainer: FC<IFormContainer> = ({
  form,
  handleChange,
  handleChangeCountry,
}) => {

  // eslint-disable-next-line no-console
  console.log({form})
  /**
   * Renders a form field based on the provided parameters.
   *
   * @param {any} params - The field configuration parameters.
   * @param {number} index - The index of the field in the form array.
   *
   * @returns {JSX.Element} The rendered form field.
   */
  const renderform = useCallback(
    (params: any, index: number) => {
      const { type, validation, formatter, width, ...rest } = params ?? {};
      switch (type) {
        case "input": {
          const props = {
            ...rest,

            handleChange: (event: any) => {
              handleChange({ ...event, index, validation, formatter });
            },
            ...(rest.isCountryCodeVisible &&
              handleChangeCountry && {
                handleChangeCountry: (value: any) =>
                  handleChangeCountry(value.label, index),
              }),
          };
          return (
            <div style={{ width }}>
              <Input {...props} />
            </div>
          );
        }

        default:
          return <></>; // Return an empty fragment if type is not handled
      }
    },
    [handleChange, handleChangeCountry] // Dependencies for useCallback
  );

  /**
   * Memoized component rendering form fields based on the form configuration.
   *
   * @returns {JSX.Element[]} An array of rendered form fields.
   */

  const renderComponent = useMemo(
    () =>
      form?.map((el: any, index: number) => (
        <Fragment key={el.name + index.toString()}>
          {!el.isHidden && renderform(el, index)}{" "}
          {/* Render form field if not hidden */}
        </Fragment>
      )),
    [form, renderform] // Dependencies for useMemo
  );

  return <>{renderComponent}</>; // Return the array of rendered form fields
};
